import environment from "../environment/index.js";

/**
 * Multi-Tenant Subdomain & Domain Architecture
 * Base staging domain: iknow.jcsoftwaresolution.in
 *
 * Supported Patterns:
 * 1. Tenant Admin Portal:
 *    https://<subdomain>.admin.iknow.jcsoftwaresolution.in
 *    Example: https://pardeep.admin.iknow.jcsoftwaresolution.in
 *
 * 2. Tenant Customer Storefront / Portal:
 *    https://<subdomain>.frontend.iknow.jcsoftwaresolution.in
 *    Example: https://pardeep.frontend.iknow.jcsoftwaresolution.in
 *
 * 3. Localhost Development:
 *    - http://<subdomain>.admin.localhost:3000
 *    - http://<subdomain>.frontend.localhost:3000
 *    - Or query parameter override: ?tenant=pardeep&portal=admin (or portal=frontend)
 *
 * 4. Legacy single-tier fallback:
 *    https://<subdomain>.iknow.jcsoftwaresolution.in
 */

export const getBaseDomain = () => {
  if (typeof window === "undefined") return "iknow.jcsoftwaresolution.in";
  const hostname = (window.location.hostname || "").toLowerCase();

  if (hostname.includes("jcsoftwaresolution.in")) {
    return "iknow.jcsoftwaresolution.in";
  }
  if (hostname.includes("charlessmithassociates.com")) {
    return "charlessmithassociates.com";
  }
  return environment.base_domain || "iknow.jcsoftwaresolution.in";
};

/**
 * Returns formatted Tenant Admin Portal URL for a given slug
 * e.g. https://pardeep.admin.iknow.jcsoftwaresolution.in
 */
export const getTenantAdminUrl = (slug) => {
  if (!slug) return "";
  const cleanSlug = String(slug).toLowerCase().trim();

  if (typeof window !== "undefined") {
    const hostname = (window.location.hostname || "").toLowerCase();
    const port = window.location.port ? `:${window.location.port}` : "";
    const protocol = window.location.protocol || "https:";

    // Localhost testing
    if (hostname.includes("localhost") || hostname === "127.0.0.1") {
      return `http://${cleanSlug}.admin.localhost${port}`;
    }
  }

  const baseDomain = getBaseDomain();
  return `https://${cleanSlug}.admin.${baseDomain}`;
};

/**
 * Returns formatted Tenant Customer / Storefront Portal URL for a given slug
 * e.g. https://pardeep.frontend.iknow.jcsoftwaresolution.in
 */
export const getTenantFrontendUrl = (slug) => {
  if (!slug) return "";
  const cleanSlug = String(slug).toLowerCase().trim();

  if (typeof window !== "undefined") {
    const hostname = (window.location.hostname || "").toLowerCase();
    const port = window.location.port ? `:${window.location.port}` : "";
    const protocol = window.location.protocol || "https:";

    // Localhost testing
    if (hostname.includes("localhost") || hostname === "127.0.0.1") {
      return `http://${cleanSlug}.frontend.localhost${port}`;
    }
  }

  const baseDomain = getBaseDomain();
  return `https://${cleanSlug}.frontend.${baseDomain}`;
};

/**
 * Comprehensive multi-tenant detection from current window.location
 */
export const getTenantInfo = () => {
  if (typeof window === "undefined") {
    return {
      slug: null,
      portalType: "root", // 'admin' | 'frontend' | 'root'
      isTenant: false,
      isTenantAdmin: false,
      isTenantFrontend: false,
      isLogistics: false,
      source: "ssr",
      hostname: "",
      baseDomain: "iknow.jcsoftwaresolution.in",
      adminUrl: "",
      frontendUrl: "",
    };
  }

  const hostname = (window.location.hostname || "").toLowerCase();
  const searchParams = new URLSearchParams(window.location.search || "");
  const baseDomain = getBaseDomain();

  // 1. Query parameter override (ideal for local testing: ?tenant=pardeep&portal=admin)
  const queryTenant = searchParams.get("tenant") || searchParams.get("subdomain");
  if (queryTenant) {
    const cleanSlug = queryTenant.trim().toLowerCase();
    const queryPortal = (searchParams.get("portal") || "").toLowerCase();
    const portalType = queryPortal === "frontend" ? "frontend" : "admin";

    sessionStorage.setItem("active_tenant_slug", cleanSlug);
    sessionStorage.setItem("active_tenant_portal", portalType);

    return {
      slug: cleanSlug,
      portalType,
      isTenant: true,
      isTenantAdmin: portalType === "admin",
      isTenantFrontend: portalType === "frontend",
      isLogistics: false,
      source: "query",
      hostname,
      baseDomain,
      adminUrl: getTenantAdminUrl(cleanSlug),
      frontendUrl: getTenantFrontendUrl(cleanSlug),
    };
  }

  // 2. Double-subdomain pattern: <slug>.(admin|frontend).<baseDomain>
  // e.g. pardeep.admin.iknow.jcsoftwaresolution.in
  // e.g. pardeep.frontend.iknow.jcsoftwaresolution.in
  const adminRegex = new RegExp(`^([a-z0-9_-]+)\\.admin\\.${baseDomain.replace(/\./g, "\\.")}$`, "i");
  const frontendRegex = new RegExp(`^([a-z0-9_-]+)\\.frontend\\.${baseDomain.replace(/\./g, "\\.")}$`, "i");

  const adminMatch = hostname.match(adminRegex);
  if (adminMatch && adminMatch[1]) {
    const slug = adminMatch[1].toLowerCase();
    return {
      slug,
      portalType: "admin",
      isTenant: true,
      isTenantAdmin: true,
      isTenantFrontend: false,
      isLogistics: false,
      source: "subdomain",
      hostname,
      baseDomain,
      adminUrl: getTenantAdminUrl(slug),
      frontendUrl: getTenantFrontendUrl(slug),
    };
  }

  const frontendMatch = hostname.match(frontendRegex);
  if (frontendMatch && frontendMatch[1]) {
    const slug = frontendMatch[1].toLowerCase();
    return {
      slug,
      portalType: "frontend",
      isTenant: true,
      isTenantAdmin: false,
      isTenantFrontend: true,
      isLogistics: false,
      source: "subdomain",
      hostname,
      baseDomain,
      adminUrl: getTenantAdminUrl(slug),
      frontendUrl: getTenantFrontendUrl(slug),
    };
  }

  // 3. Localhost double-subdomain testing:
  // e.g. pardeep.admin.localhost OR pardeep.frontend.localhost
  if (hostname.endsWith(".localhost")) {
    const localParts = hostname.replace(/\.localhost$/, "").split(".");
    if (localParts.length >= 2) {
      const slug = localParts[0].toLowerCase();
      const portalType = localParts[1].toLowerCase() === "frontend" ? "frontend" : "admin";
      return {
        slug,
        portalType,
        isTenant: true,
        isTenantAdmin: portalType === "admin",
        isTenantFrontend: portalType === "frontend",
        isLogistics: false,
        source: "subdomain",
        hostname,
        baseDomain,
        adminUrl: getTenantAdminUrl(slug),
        frontendUrl: getTenantFrontendUrl(slug),
      };
    } else if (localParts.length === 1 && localParts[0] && localParts[0] !== "localhost") {
      // Single sub on localhost fallback
      const slug = localParts[0].toLowerCase();
      return {
        slug,
        portalType: "admin",
        isTenant: true,
        isTenantAdmin: true,
        isTenantFrontend: false,
        isLogistics: false,
        source: "subdomain",
        hostname,
        baseDomain,
        adminUrl: getTenantAdminUrl(slug),
        frontendUrl: getTenantFrontendUrl(slug),
      };
    }
  }

  // 4. Single-tier subdomain fallback: <slug>.<baseDomain>
  if (hostname.includes(baseDomain)) {
    const prefix = hostname.replace(new RegExp(`\\.?${baseDomain}$`), "").trim().toLowerCase();
    if (prefix && prefix !== "www" && prefix !== "admin" && prefix !== "frontend") {
      return {
        slug: prefix,
        portalType: "admin",
        isTenant: prefix !== "logistics",
        isTenantAdmin: prefix !== "logistics",
        isTenantFrontend: false,
        isLogistics: prefix === "logistics",
        source: "subdomain",
        hostname,
        baseDomain,
        adminUrl: getTenantAdminUrl(prefix),
        frontendUrl: getTenantFrontendUrl(prefix),
      };
    }
  }

  // 5. Stored session tenant (useful when testing on IP or localhost)
  const sessionTenant = sessionStorage.getItem("active_tenant_slug");
  const sessionPortal = sessionStorage.getItem("active_tenant_portal") || "admin";
  if (sessionTenant && (hostname.includes("74.208.13.142") || hostname === "localhost")) {
    return {
      slug: sessionTenant.toLowerCase(),
      portalType: sessionPortal,
      isTenant: true,
      isTenantAdmin: sessionPortal === "admin",
      isTenantFrontend: sessionPortal === "frontend",
      isLogistics: false,
      source: "session",
      hostname,
      baseDomain,
      adminUrl: getTenantAdminUrl(sessionTenant),
      frontendUrl: getTenantFrontendUrl(sessionTenant),
    };
  }

  // 6. Default root platform (landing page / registration)
  return {
    slug: null,
    portalType: "root",
    isTenant: false,
    isTenantAdmin: false,
    isTenantFrontend: false,
    isLogistics: false,
    source: "root",
    hostname,
    baseDomain,
    adminUrl: "",
    frontendUrl: "",
  };
};

/**
 * Returns current active tenant slug, or null if on main platform.
 */
export const getTenantSlug = () => {
  return getTenantInfo().slug;
};

/**
 * Returns current tenant portal type: 'admin' | 'frontend' | 'root'
 */
export const getTenantPortalType = () => {
  return getTenantInfo().portalType;
};

/**
 * Clears any test session tenant override.
 */
export const clearSessionTenant = () => {
  sessionStorage.removeItem("active_tenant_slug");
  sessionStorage.removeItem("active_tenant_portal");
};
